import { resolveMountBasePath } from "@/mountPath";

/**
 * Service Workerの登録。
 *
 * このmoduleはentry chunk(main.tsx)に静的に載るため、config層を一切importしない。
 * 特に `@/config/boardRegistry` はWorker bundle専用の本番env検証
 * (manifests/production.tsのtop-level throw)を連れてくるので、entryへ混入すると
 * 全ページがReact描画前に停止する(2026-08-23の本番障害)。
 */

export type ServiceWorkerTarget = Readonly<{
  scriptUrl: string;
  scope: string;
}>;

/**
 * 現在地のマウント(板 /b/{slug}、mix /mix、それ以外はroot-mounted配信)に
 * 対応するSWのscript URLとscopeを返す。dist/sw.jsはWorkerの板asset素通しで
 * どのマウント配下でも `{publicBasePath}/sw.js` として配信される。
 */
export function resolveServiceWorkerTarget(
  pathname: string,
): ServiceWorkerTarget {
  const base = resolveMountBasePath(pathname);
  return { scriptUrl: `${base}/sw.js`, scope: `${base}/` };
}

type ServiceWorkerContainerLike = {
  register(scriptUrl: string, options: { scope: string }): Promise<unknown>;
};

type NavigatorLike = { serviceWorker?: ServiceWorkerContainerLike };
type LocationLike = Pick<Location, "pathname">;

/**
 * PWA用SWを登録する。dev(vite)はdist/sw.jsを配信しないため本番buildのみ有効。
 * 非対応環境・非セキュアコンテキストでは何もしない。
 */
export async function registerServiceWorker(
  enabled: boolean = import.meta.env.PROD,
  navigatorLike: NavigatorLike | undefined = globalThis.navigator,
  locationLike: LocationLike = globalThis.location,
): Promise<boolean> {
  if (!enabled) return false;
  const container = navigatorLike?.serviceWorker;
  if (container === undefined) return false;
  const target = resolveServiceWorkerTarget(locationLike.pathname);
  try {
    await container.register(target.scriptUrl, { scope: target.scope });
    return true;
  } catch (error) {
    // 登録失敗はPWA機能の欠落に留まり、閲覧自体は妨げないため続行する
    console.warn("Service worker registration failed", error);
    return false;
  }
}
